import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { teamAPI } from '../services/api';

const AcceptInvitePage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const [invite, setInvite] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) { setError('Token de invitacion no encontrado'); setLoading(false); return; }
    teamAPI.verifyUserInvite(token)
      .then(res => { setInvite(res.data); setLoading(false); })
      .catch(err => { setError(err.response?.data?.detail || 'Invitacion invalida o expirada'); setLoading(false); });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { setError('Las contrasenas no coinciden'); return; }
    if (password.length < 6) { setError('La contrasena debe tener al menos 6 caracteres'); return; }
    setSubmitting(true);
    setError(null);
    try {
      await teamAPI.acceptUserInvite({ token, password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear la cuenta');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center px-4">
      <div className="w-full max-w-md" data-testid="accept-invite-page">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-green-400">TKorveX</h1>
          <p className="text-gray-400 text-sm mt-1">Decisiones que generan valor</p>
        </div>

        <div className="bg-gray-800 rounded-2xl border border-gray-700 p-6 shadow-xl">
          {success ? (
            <div className="text-center space-y-4" data-testid="invite-success">
              <div className="w-16 h-16 bg-green-900/40 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-white">Cuenta Creada</h2>
              <p className="text-gray-400 text-sm">Tu cuenta ha sido creada exitosamente. Ya podes iniciar sesion.</p>
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-medium transition"
                data-testid="go-to-login"
              >
                Iniciar Sesion
              </button>
            </div>
          ) : error && !invite ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-red-900/40 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-white">Invitacion Invalida</h2>
              <p className="text-gray-400 text-sm">{error}</p>
              <button onClick={() => navigate('/login')} className="text-green-400 hover:text-green-300 text-sm transition">
                Ir al Login
              </button>
            </div>
          ) : invite && (
            <div>
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-white">Bienvenido al Equipo</h2>
                <p className="text-gray-400 text-sm mt-2">
                  <span className="text-green-400 font-medium">{invite.admin_nombre}</span> te invito a unirte
                </p>
              </div>

              {/* Info */}
              <div className="bg-gray-700/50 rounded-lg p-4 mb-5 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Email</span>
                  <span className="text-white font-medium">{invite.email}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Nombre</span>
                  <span className="text-white font-medium">{invite.nombre}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Rol</span>
                  <span className={`font-medium ${invite.rol === 'operador' ? 'text-green-400' : 'text-amber-400'}`}>
                    {invite.rol === 'operador' ? 'Operador (Acceso Completo)' : 'Visor (Solo Lectura)'}
                  </span>
                </div>
              </div>

              {error && (
                <div className="bg-red-900/30 border border-red-800 text-red-400 px-4 py-2 rounded-lg text-sm mb-4">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Crear Contrasena</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg outline-none focus:ring-2 focus:ring-green-500 text-sm"
                    placeholder="Minimo 6 caracteres"
                    data-testid="invite-password"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Confirmar Contrasena</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg outline-none focus:ring-2 focus:ring-green-500 text-sm"
                    placeholder="Repetir contrasena"
                    data-testid="invite-confirm-password"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-medium disabled:opacity-50 transition"
                  data-testid="accept-invite-submit"
                >
                  {submitting ? 'Creando cuenta...' : 'Aceptar Invitacion y Crear Cuenta'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitePage;
